import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppDispatch } from 'src/store/hooks'
import { showAlert } from 'src/actions/alert.actions'
import { handlerError } from 'src/selectors/error.selector'
import { ClientBalanceResponse } from 'src/utils/clientIdentification'

export const DEFAULT_PAGE_SIZE = 10

export type FetchPage = (
  params: any,
) => Promise<{ list: ClientBalanceResponse[]; total: number }>

/**
 * Estado paginado local de clientes con saldo (sin Redux). El endpoint entra
 * por `fetchPage`; `extraParams` (attendant/categorization/type) se mezclan al
 * request. Guard `requestRef` descarta respuestas fuera de orden. Recarga la
 * página 0 al cambiar `extraParams` o `reloadKey`. Patrón: ATL-323.
 */
export const useClientBalanceList = (
  fetchPage: FetchPage,
  extraParams: object,
  reloadKey = 0,
) => {
  const dispatch = useAppDispatch()

  const [items, setItems] = useState<ClientBalanceResponse[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const requestRef = useRef(0)
  const paramsRef = useRef(extraParams)
  paramsRef.current = extraParams

  useEffect(() => () => void requestRef.current++, [])

  const load = useCallback(
    async (skip: number, size: number, search?: string) => {
      const requestId = ++requestRef.current
      setLoading(true)
      try {
        const data = await fetchPage({
          skip,
          size,
          search: search || undefined,
          ...paramsRef.current,
        })
        if (requestId !== requestRef.current) return
        setItems(data.list || [])
        setTotal(data.total || 0)
      } catch {
        if (requestId === requestRef.current)
          dispatch(showAlert(handlerError('No se pudieron cargar los clientes.')))
      } finally {
        if (requestId === requestRef.current) setLoading(false)
      }
    },
    [fetchPage, dispatch],
  )

  // Carga inicial y recarga ante cambios de filtros externos.
  useEffect(() => {
    load(0, DEFAULT_PAGE_SIZE, '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(extraParams), reloadKey])

  return { items, total, loading, load }
}
